import { Link, useSearch } from '@tanstack/react-router'
import { useMutation } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { INDUSTRIES, PLANS, getIndustry, getLocale } from '@shared/catalog'
import { presetInputFor } from '@shared/pricing'
import type { IndustryId, LocaleId, QuoteInput } from '@shared/types'
import { MarketingShell } from '../components/Layout'
import { PricingConfigurator, QuoteSummary } from '../components/PricingConfigurator'
import { api, ApiError } from '../lib/api'
import { Banner, Button, Card, Chip, TextField } from '../ui'

const FAQS = [
  {
    q: 'Do I need a WhatsApp Business API account?',
    a: (
      <>
        No. Wami connects regular WhatsApp numbers, including numbers already using the WhatsApp
        Business app. There is no Meta approval step and no message template queue. That also
        means Wami connects the same way WhatsApp Web does, not through Meta&rsquo;s official API
        &mdash; <Link to="/trust">see the full picture</Link> before connecting a number you
        couldn&rsquo;t afford to lose.
      </>
    ),
  },
  {
    q: 'Why does the price change when I switch region?',
    a: 'Prices are set per market rather than converted from a single dollar figure, so what you pay reflects local purchasing power. Tax is applied on top according to your region.',
  },
  {
    q: 'Why can I not remove some modules?',
    a: 'Healthcare and financial services carry legal requirements that the product cannot meet without the relevant compliance module. Those stay selected for as long as the industry is set.',
  },
  {
    q: 'What counts as a conversation?',
    a: 'A conversation is a thread with a single contact or group in a calendar month, regardless of how many messages it contains. Internal notes do not count.',
  },
  {
    q: 'Can I change plan later?',
    a: 'Yes. Plan, seats, numbers, and modules are all editable from the billing screen and take effect on the next cycle. Downgrades keep your data.',
  },
  {
    q: 'What happens at the end of the trial?',
    a: 'Fourteen days, no card required. At the end you choose a plan or the workspace pauses. Nothing is deleted for 90 days.',
  },
]

export function Pricing() {
  const search = useSearch({ strict: false }) as { industry?: string; locale?: string }
  const initialIndustry = (getIndustry(search.industry ?? '')?.id ?? 'general') as IndustryId
  const initialLocale = (getLocale(search.locale ?? '')?.id ?? 'us') as LocaleId

  const [input, setInput] = useState<QuoteInput>(() =>
    presetInputFor(initialIndustry, initialLocale, 'monthly'),
  )
  const industry = getIndustry(input.industryId)!
  const plan = PLANS.find((p) => p.id === input.planId)

  const [salesEmail, setSalesEmail] = useState('')
  const leadQuote = useMutation({ mutationFn: () => api.saveLeadQuote(input, salesEmail) })
  const submitLeadQuote = (event: FormEvent) => {
    event.preventDefault()
    if (salesEmail.trim()) leadQuote.mutate()
  }
  // Switching plans mid-flow — say, out of Enterprise and back in — must not leave a stale
  // "we already emailed sales" banner up for a configuration nobody actually submitted yet.
  const updateInput = (next: QuoteInput) => {
    if (next.planId !== input.planId) leadQuote.reset()
    setInput(next)
  }

  return (
    <MarketingShell>
      <section className="container section stack gap-5" style={{ paddingBlock: '56px 32px' }}>
        <div className="stack gap-3" style={{ maxWidth: '60ch' }}>
          <span className="eyebrow">Pricing</span>
          <h1 className="display-small">Configure it, then see the real number.</h1>
          <p className="body-large text-muted">
            This calculator runs the same pricing engine that generates your invoice. Pick an
            industry to load a working default, then change anything.
          </p>
        </div>
      </section>

      <section className="container" style={{ paddingBottom: '48px' }}>
        <Card variant="filled">
          <div className="stack gap-3">
            <div className="row gap-2 wrap items-baseline">
              <span className="display-small" style={{ fontSize: '2.25rem' }}>
                $49/month flat
              </span>
              <span className="body-large text-muted">for 3 seats, 1 number, 2,000 real conversations</span>
            </div>
            <p className="body-medium text-muted" style={{ maxWidth: '68ch' }}>
              A conversation is a real thread, not a metered AI credit or a broadcast-contact
              count — the number above is what you actually get, and it's what your invoice will
              say. No per-message surprise, no credit top-ups to buy mid-month.
            </p>
            <Link to="/compare" className="body-medium">
              See how that holds up against Gallabox and Periskope's entry pricing →
            </Link>
          </div>
        </Card>
      </section>

      <section className="container" style={{ paddingBottom: '80px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1.55fr) minmax(300px, 1fr)',
            gap: '32px',
            alignItems: 'start',
          }}
          className="pricing-grid"
        >
          <Card variant="outlined">
            <div className="stack gap-5">
              <Banner tone="info">
                <div className="stack gap-1">
                  <strong className="title-small">{industry.name} preset applied</strong>
                  <span>{industry.presetRationale}</span>
                </div>
              </Banner>
              <PricingConfigurator input={input} onChange={updateInput} />
            </div>
          </Card>

          <div style={{ position: 'sticky', top: '88px' }} className="stack gap-4">
            <Card variant="filled">
              <QuoteSummary input={input} collapseBreakdown />
            </Card>
            {plan?.contactSales ? (
              leadQuote.isSuccess ? (
                <Banner tone="success">
                  Thanks — we&rsquo;ve got your {industry.name.toLowerCase()} configuration and someone
                  from sales will reach out to {salesEmail} shortly.
                </Banner>
              ) : (
                <form className="stack gap-3" onSubmit={submitLeadQuote}>
                  <TextField
                    label="Work email"
                    type="email"
                    required
                    value={salesEmail}
                    onChange={(e) => setSalesEmail(e.target.value)}
                    placeholder="you@company.com"
                  />
                  {leadQuote.isError && (
                    <Banner tone="error">
                      {leadQuote.error instanceof ApiError ? leadQuote.error.message : 'That did not go through.'}
                    </Banner>
                  )}
                  <Button type="submit" size="large" full disabled={!salesEmail.trim() || leadQuote.isPending}>
                    {leadQuote.isPending ? 'Sending…' : 'Talk to sales'}
                  </Button>
                  <p className="body-small text-muted" style={{ textAlign: 'center' }}>
                    We&rsquo;ll send this configuration along so sales starts from the real number.
                  </p>
                </form>
              )
            ) : (
              <>
                <Link to="/signup" search={{ industry: input.industryId }} className="btn btn-filled btn-large btn-full">
                  Start free trial
                </Link>
                <p className="body-small text-muted" style={{ textAlign: 'center' }}>
                  14 days free. No card required.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      <section
        className="section"
        style={{ background: 'var(--md-surface-container-low)', borderBlock: '1px solid var(--md-outline-variant)' }}
      >
        <div className="container stack gap-5">
          <h2 className="headline-medium">What each plan includes</h2>
          <div className="scroll-x">
            <table className="table" style={{ minWidth: '760px' }}>
              <thead>
                <tr>
                  <th style={{ width: '30%' }}>Plan</th>
                  <th>Included</th>
                  <th>Best for</th>
                </tr>
              </thead>
              <tbody>
                {PLANS.map((plan) => (
                  <tr key={plan.id}>
                    <td>
                      <div className="stack gap-2">
                        <div className="row gap-2 wrap">
                          <span className="title-medium">{plan.name}</span>
                          {plan.popular && <Chip tone="warn">Popular</Chip>}
                        </div>
                        <span className="body-medium text-muted">
                          {plan.contactSales ? 'Custom pricing' : `From $${plan.baseCents / 100}/month`}
                        </span>
                      </div>
                    </td>
                    <td>
                      <ul className="stack gap-1" style={{ paddingLeft: '18px', margin: 0 }}>
                        {plan.features.map((f) => (
                          <li key={f} className="body-medium">
                            {f}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="body-medium text-muted">{plan.tagline}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="container section stack gap-5">
        <h2 className="headline-medium">Industry presets</h2>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '16px',
          }}
        >
          {INDUSTRIES.map((i) => (
            <Card key={i.id} variant="outlined">
              <div className="stack gap-3">
                <div className="stack gap-1">
                  <h3 className="title-large">{i.name}</h3>
                  <span className="body-small text-muted">
                    Starts on {i.presets.plan}, {i.presets.seats} seats, {i.presets.numbers} numbers
                  </span>
                </div>
                <p className="body-medium text-muted">{i.presetRationale}</p>
                <button
                  type="button"
                  className="btn btn-tonal"
                  onClick={() => updateInput(presetInputFor(i.id, input.localeId, input.billingCycle))}
                >
                  Load this preset
                </button>
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section
        className="section"
        style={{ background: 'var(--md-surface-container-low)', borderTop: '1px solid var(--md-outline-variant)' }}
      >
        <div className="container-narrow stack gap-4">
          <h2 className="headline-medium">Questions</h2>
          <div className="stack gap-2">
            {FAQS.map((f) => (
              <details
                key={f.q}
                style={{
                  padding: '16px 20px',
                  borderRadius: 'var(--md-shape-md)',
                  background: 'var(--md-surface)',
                  boxShadow: 'inset 0 0 0 1px var(--md-outline-variant)',
                }}
              >
                <summary className="title-medium" style={{ cursor: 'pointer' }}>
                  {f.q}
                </summary>
                <p className="body-medium text-muted" style={{ marginTop: '8px' }}>
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </MarketingShell>
  )
}
